import {DeepLink, DeepLinkUrl} from "@app/core/shared/domain/entities/deep-link";
import {DeepLinkPath} from "@app/core/shared/infrastructure/deep-link.path";

export class DeepLinkMapper {
    private readonly CUSTOM_URL_SCHEME = 'com.europastry.ecommerce';
    private readonly FILTERS_QUERY_PARAM_KEY = 'f';

    /**
     * Map the url to a DeepLink object.
     * If the url doesn't match any of the above, we return null.
     * @param url the deep link url received from the appUrlOpen event
     * @private
     */
     mapUrlToDeepLink(url: string): DeepLink | null {
        const pathWithSerializedQueryParams = this.extractPathWithQueryParams(url);
        // Check if the url has a custom url scheme.
        if(this.hasUrlScheme(url)) {
            const path = this.extractPathFromCustomUrlScheme(url,this.CUSTOM_URL_SCHEME);
            if (path.startsWith('checkout')) {
                return new DeepLink(DeepLinkUrl.CHECKOUT);
            }
        }
        return this.mapPathWithQueryParamsToDeepLink(pathWithSerializedQueryParams);
    }

    /**
     * Map the path with serialized query params to a DeepLink object.
     * If the path doesn't match any of the above, we return null.
     * @param pathWithSerializedQueryParams
     */
    mapPathWithQueryParamsToDeepLink(pathWithSerializedQueryParams: string): DeepLink | null {
        const path = this.extractPath(pathWithSerializedQueryParams);
        const slugs = this.extractSlugsFromPath(path);
        const queryParams = this.extractSerializedQueryParams(pathWithSerializedQueryParams);

        //Return the deep link depending on the path
        switch (true) {
            case path.startsWith(DeepLinkPath.HOME):
            case path === '/':
                return new DeepLink(DeepLinkUrl.HOME);
            case path.startsWith(DeepLinkPath.ORDERS):
                return new DeepLink(DeepLinkUrl.ORDERS);
            case path.startsWith(DeepLinkPath.COUPONS):
                return new DeepLink(DeepLinkUrl.COUPONS);
            case path.startsWith(DeepLinkPath.LISTS):
                return new DeepLink(DeepLinkUrl.LISTS);
            case path.startsWith(DeepLinkPath.USUAL_PURCHASE):
                return new DeepLink(DeepLinkUrl.USUAL_PURCHASE);
            case path.startsWith(DeepLinkPath.FAVORITES):
                return new DeepLink(DeepLinkUrl.FAVORITES);
            case path.startsWith(DeepLinkPath.PROMOTIONS):
                return new DeepLink(DeepLinkUrl.PROMOTIONS);
            case path.startsWith(DeepLinkPath.NEWS):
                return new DeepLink(DeepLinkUrl.NEWS);
            case path.startsWith(DeepLinkPath.BEST_SELLERS):
                return new DeepLink(DeepLinkUrl.BEST_SELLERS);
            case path.startsWith(DeepLinkPath.CATEGORIES):
                return new DeepLink(DeepLinkUrl.CATEGORIES);
            case path.startsWith(DeepLinkPath.CATALOG):
                const catalogParameters = {
                    categoryCode: slugs[1], // The category code is the second slug.
                    filters: queryParams.get(this.FILTERS_QUERY_PARAM_KEY),
                };
                return new DeepLink(DeepLinkUrl.CATALOG, catalogParameters);
            case path.startsWith(DeepLinkPath.COMPARATOR):
                return new DeepLink(DeepLinkUrl.COMPARATOR);
            case path.startsWith(DeepLinkPath.CHANGE_EMAIL):
                return new DeepLink(DeepLinkUrl.CHANGE_EMAIL);
            case path.startsWith(DeepLinkPath.CHANGE_PASSWORD):
                return new DeepLink(DeepLinkUrl.CHANGE_PASSWORD);
            case path.startsWith(DeepLinkPath.CHECKOUT):
                return new DeepLink(DeepLinkUrl.CHECKOUT);
            case path.startsWith(DeepLinkPath.ACCOUNT):
                return new DeepLink(DeepLinkUrl.ACCOUNT);
            case path.startsWith(DeepLinkPath.PRODUCT_DETAIL):
                const productParameters = {
                    productId: slugs[1], // The product id is the second slug.
                };
                return new DeepLink(DeepLinkUrl.PRODUCT_DETAIL, productParameters);
            case path.startsWith(DeepLinkPath.PROMOTION_DETAIL):
                return new DeepLink(DeepLinkUrl.PROMOTION_DETAIL, {promotionId: slugs[1]});
            case path.startsWith(DeepLinkPath.LOGIN):
                const loginParameters = {
                    user: queryParams.get('user'),
                    password: queryParams.get('pwd'),
                }
                return new DeepLink(DeepLinkUrl.LOGIN,loginParameters);
            case path.startsWith(DeepLinkPath.SHARED):
                const sharedParameters = {
                    products: queryParams.get('products'),
                }
                return new DeepLink(DeepLinkUrl.SHARED, sharedParameters);
            default:
                return null;
        }
    }

    /**
     * Extract only the path from path with serialized query params.
     * @param pathWithSerializedQueryParams
     * @private
     */
    private extractPath(pathWithSerializedQueryParams: string) {
        return pathWithSerializedQueryParams.split('?')[0];
    }

    /**
     * Extract the path from the deep link url depending on the platform.
     * @param url the deep link url
     * @private
     */
    private extractPathWithQueryParams(url: string): string {
        const parsedUrl = new URL(url);
        const pathFromQueryParam = new URL(parsedUrl).searchParams.get('deepLink');
        //If the url has a deepLink query param, we use it as the path with query params.
        if (pathFromQueryParam) {
            return pathFromQueryParam;
        }
        //If the url doesn't have a deepLink query param, we use the path with serialized query params.
        return parsedUrl.pathname + parsedUrl.search;
    }

    /**
     * Extract the serialized query params from the path with serialized query params.
     * @param pathWithQueryParams
     * @private
     */
    private extractSerializedQueryParams(pathWithQueryParams: string): URLSearchParams {
        const queryPramsString = pathWithQueryParams.split('?')[1];
        return new URLSearchParams(queryPramsString);
    }

    /**
     * Extract the slugs from a path.
     * @param path
     * @private
     */
    private extractSlugsFromPath(path: string): string[] {
        //Split the path by '/' and remove the first empty element when the path starts with '/'.
        return path.split('/').filter(slug => slug !== '');
    }

    /**
     * Check if the url has custom url scheme.
     * @param url
     * @private
     */
    private hasUrlScheme(url: string): boolean {
        const parsedUrl = new URL(url);
        return parsedUrl.protocol === `${this.CUSTOM_URL_SCHEME}:`;
    }

    /**
     * It takes a URL and a custom URL scheme, and returns the path of the URL after the custom URL scheme
     * @param {string} url - The URL that was passed to the app.
     * @param {string} customUrlScheme - The custom URL scheme you've set up in your app's Info.plist file.
     * @returns The path of the url.
     */
    private extractPathFromCustomUrlScheme(url: string, customUrlScheme: string): string {
        return url.replace(`${customUrlScheme}://`, '');
    }
}

